import React, { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Button,
  Autocomplete,
  AutocompleteItem,
  Input,
  CircularProgress,
  Checkbox,
} from "@nextui-org/react";
import { FiFlag } from "react-icons/fi";
import { subDays, format } from "date-fns";

import { isOperatorActive } from "./debriefUtils";
import { createDebrief, fetchOperatorKpis } from "../../redux/actions/debriefActions";

// `defaultScheduledDate` ("yyyy-MM-dd", optional) pre-ticks the schedule
// checkbox with that date, e.g. when opened from a day of the calendar view.
const CreateDebriefModal = ({
  operators,
  onClose,
  onSuccess,
  defaultOperatorId = "",
  defaultScheduledDate = "",
}) => {
  const { t } = useTranslation();

  const [selectedOperator, setSelectedOperator] = useState(defaultOperatorId);
  const [periodStart, setPeriodStart] = useState(format(subDays(new Date(), 14), "yyyy-MM-dd"));
  const [periodEnd, setPeriodEnd] = useState(format(new Date(), "yyyy-MM-dd"));
  const [loading, setLoading] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [kpiPreview, setKpiPreview] = useState(null);
  const [operatorSearch, setOperatorSearch] = useState("");
  const isSelectingRef = useRef(false);
  const [wantsSchedule, setWantsSchedule] = useState(!!defaultScheduledDate);
  const [scheduledDate, setScheduledDate] = useState(defaultScheduledDate || "");
  const [error, setError] = useState("");

  const handlePreviewKpis = async () => {
    if (!selectedOperator || !periodStart || !periodEnd) {
      setError(t("Veuillez sélectionner un représentant et une période"));
      return;
    }

    setPreviewLoading(true);
    setError("");

    try {
      const startDate = new Date(periodStart).toISOString();
      const endDate = new Date(periodEnd).toISOString();

      const kpis = await fetchOperatorKpis(selectedOperator, startDate, endDate);
      setKpiPreview(kpis);
    } catch (err) {
      setError(t("Échec du chargement des KPIs"));
      console.error(err);
    }

    setPreviewLoading(false);
  };

  const handleCreate = async () => {
    if (!selectedOperator || !periodStart || !periodEnd) {
      setError(t("Veuillez sélectionner un représentant et une période"));
      return;
    }

    setLoading(true);
    setError("");

    try {
      const startDate = new Date(periodStart).toISOString();
      const endDate = new Date(periodEnd).toISOString();
      const scheduledDateISO =
        wantsSchedule && scheduledDate ? new Date(scheduledDate).toISOString() : null;

      const debriefId = await createDebrief(selectedOperator, startDate, endDate, scheduledDateISO);

      if (debriefId) {
        onSuccess(debriefId);
      } else {
        setError(t("Échec de la création du debrief"));
      }
    } catch (err) {
      setError(t("Échec de la création du debrief"));
      console.error(err);
    }

    setLoading(false);
  };

  // Deactivated operators are hidden, except the preselected one so a default
  // pointing at an inactive operator doesn't silently vanish from the field.
  const nonAdminOperators = React.useMemo(
    () =>
      operators.filter(
        (op) => !op?.user?.is_easier_admin && (isOperatorActive(op) || op.id === defaultOperatorId)
      ),
    [operators, defaultOperatorId]
  );

  const filteredOperators = React.useMemo(() => {
    if (!operatorSearch) return nonAdminOperators;
    const search = operatorSearch.toLowerCase();
    return nonAdminOperators.filter(
      (op) =>
        op.first_name?.toLowerCase().includes(search) ||
        op.last_name?.toLowerCase().includes(search) ||
        op.email?.toLowerCase().includes(search)
    );
  }, [nonAdminOperators, operatorSearch]);

  const getSelectedOperatorName = () => {
    const op = operators.find((o) => o.id === selectedOperator);
    return op ? `${op.first_name} ${op.last_name}` : "";
  };

  return (
    <div className="p-6">
      <h2 className="text-xl font-bold mb-6">{t("Créer un nouveau debrief")}</h2>

      <div className="space-y-4">
        {/* Operator Selection */}
        <Autocomplete
          label={t("Représentant")}
          placeholder={t("Sélectionner un représentant")}
          selectedKey={selectedOperator || null}
          onSelectionChange={(key) => {
            isSelectingRef.current = true;
            setSelectedOperator(key || "");
            setKpiPreview(null);
          }}
          onInputChange={(value) => {
            if (isSelectingRef.current) {
              isSelectingRef.current = false;
              return;
            }
            setOperatorSearch(value);
          }}
          isRequired
          isClearable
          items={filteredOperators}
        >
          {(op) => (
            <AutocompleteItem
              key={op.id}
              textValue={`${op.first_name} ${op.last_name}`}
            >
              <div className="flex flex-col">
                <span>{`${op.first_name} ${op.last_name}`}</span>
                {op.email && <span className="text-xs text-default-400">{op.email}</span>}
              </div>
            </AutocompleteItem>
          )}
        </Autocomplete>

        {/* Date Range */}
        <div className="flex gap-4">
          <Input
            type="date"
            label={t("Début de période")}
            placeholder=" "
            value={periodStart}
            onChange={(e) => {
              setPeriodStart(e.target.value);
              setKpiPreview(null);
            }}
            isRequired
          />
          <Input
            type="date"
            label={t("Fin de période")}
            placeholder=" "
            value={periodEnd}
            onChange={(e) => {
              setPeriodEnd(e.target.value);
              setKpiPreview(null);
            }}
            isRequired
          />
        </div>

        {/* Schedule checkbox + date */}
        <Checkbox
          isSelected={wantsSchedule}
          onValueChange={(checked) => {
            setWantsSchedule(checked);
            if (!checked) setScheduledDate("");
          }}
        >
          {t("Je veux planifier un débrief dans le futur")}
        </Checkbox>
        <div>
          {wantsSchedule && (
            <Input
              type="date"
              label={t("Date planifiée")}
              placeholder=" "
              className="mb-4"
              value={scheduledDate}
              onChange={(e) => setScheduledDate(e.target.value)}
            />
          )}

          {/* Preview Button */}
          <Button
            variant="bordered"
            color="primary"
            onClick={handlePreviewKpis}
            isLoading={previewLoading}
            isDisabled={!selectedOperator || !periodStart || !periodEnd}
          >
            {t("Aperçu des KPIs")}
          </Button>
        </div>

        {/* KPI Preview */}
        {kpiPreview && (
          <div className="bg-gray-50 rounded-lg p-4 mt-4">
            <h3 className="font-semibold mb-3">
              {t("Aperçu KPI pour")} {getSelectedOperatorName()}
            </h3>
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-white rounded p-3">
                <div className="text-sm text-gray-500">{t("Total appels")}</div>
                <div className="text-xl font-bold">{kpiPreview.kpi_snapshot?.total_calls || 0}</div>
              </div>
              <div className="bg-white rounded p-3">
                <div className="text-sm text-gray-500">{t("Note moyenne")}</div>
                <div className="text-xl font-bold">
                  {kpiPreview.kpi_snapshot?.average_grade >= 0
                    ? (Math.round(kpiPreview.kpi_snapshot.average_grade * 10) / 10).toFixed(1)
                    : "-"}
                </div>
              </div>
              <div className="bg-white rounded p-3">
                <div className="text-sm text-gray-500">{t("Appels verts")}</div>
                <div className="text-xl font-bold text-green-600">
                  {kpiPreview.kpi_snapshot?.green_calls || 0}
                </div>
              </div>
              <div className="bg-white rounded p-3">
                <div className="text-sm text-gray-500">{t("Appels rouges")}</div>
                <div className="text-xl font-bold text-red-600">
                  {kpiPreview.kpi_snapshot?.red_calls || 0}
                </div>
              </div>
              <div className="bg-white rounded p-3">
                <div className="text-sm text-gray-500">{t("Clients frustrés")}</div>
                <div className="text-xl font-bold text-orange-600">
                  {kpiPreview.kpi_snapshot?.frustrated_clients || 0}
                </div>
              </div>
              <div className="bg-white rounded p-3">
                <div className="text-sm text-gray-500">{t("Appels par jour")}</div>
                <div className="text-xl font-bold">
                  {kpiPreview.kpi_snapshot?.calls_per_day || 0}
                </div>
              </div>
            </div>

            {/* Objectives Preview */}
            {kpiPreview.objectives_snapshot?.length > 0 && (
              <div className="mt-4">
                <h4 className="font-medium mb-2">{t("Objectifs actifs")}</h4>
                <div className="space-y-2">
                  {kpiPreview.objectives_snapshot.map((obj, idx) => (
                    <div
                      key={idx}
                      className="bg-white rounded-xl p-2 text-sm flex items-center gap-2"
                    >
                      <FiFlag
                        size={18}
                        className="text-gray-400 shrink-0"
                      />
                      <span className="font-sm">{obj.name}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Error Message */}
        {error && <div className="text-red-500 text-sm">{error}</div>}

        {/* Actions */}
        <div className="flex justify-end gap-3 mt-6 pt-4 border-t">
          <Button
            variant="light"
            onClick={onClose}
            isDisabled={loading}
          >
            {t("Annuler")}
          </Button>
          <Button
            color="primary"
            onClick={handleCreate}
            isLoading={loading}
            isDisabled={!selectedOperator || !periodStart || !periodEnd}
          >
            {t("Créer le debrief")}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default CreateDebriefModal;
